// src/components/reports/FileUpload.js
import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

const FileUpload = ({ onFileSelect, selectedFile, selectedFiles, error, multiple = false }) => {
  const { t } = useTranslation();
  const files = multiple ? (selectedFiles || []) : (selectedFile ? [selectedFile] : []);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      if (multiple) {
        const current = selectedFiles || [];
        const merged = [...current, ...acceptedFiles].slice(0, 10);
        onFileSelect(merged);
      } else {
        onFileSelect(acceptedFiles[0]);
      }
    }
  }, [onFileSelect, multiple, selectedFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
      'video/*': ['.mp4', '.mov', '.avi', '.webm'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxSize: 100 * 1024 * 1024, // 100MB
    multiple: multiple,
    maxFiles: multiple ? 10 : 1,
  });

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="w-full">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-shayek-primary dark:border-shayek-red-light bg-shayek-primary/10 dark:bg-shayek-primary/20'
            : error
            ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
            : 'border-gray-300 dark:border-shayek-dark-200 bg-gray-50 dark:bg-shayek-dark-50 hover:border-shayek-primary dark:hover:border-shayek-red-light hover:bg-shayek-primary/5 dark:hover:bg-shayek-primary/10'
        }`}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center gap-2">
          <svg
            className="w-12 h-12 text-gray-400 dark:text-shayek-dark-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          {isDragActive ? (
            <p className="text-shayek-primary dark:text-shayek-red-light font-medium">{t('report.dragDrop')}</p>
          ) : (
            <>
              <p className="text-gray-600 dark:text-shayek-dark-600 font-medium">{t('report.selectFile')}</p>
              <p className="text-sm text-gray-500 dark:text-shayek-dark-500">{multiple ? t('report.dragDropMultiple', 'Drag and drop files here, or click to select (up to 10)') : t('report.dragDrop')}</p>
            </>
          )}
        </div>
      </div>

      {files.length > 0 && (
        <div className="mt-4 space-y-2">
          {files.map((file, idx) => (
            <div key={idx} className="p-4 bg-gray-50 dark:bg-shayek-dark-50 rounded-lg border border-gray-200 dark:border-shayek-dark-200 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <svg
                  className="w-8 h-8 flex-shrink-0 text-shayek-primary dark:text-shayek-red-light"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 dark:text-shayek-dark-900 truncate">{file.name}</p>
                  <p className="text-sm text-gray-500 dark:text-shayek-dark-500">{formatFileSize(file.size)}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (multiple) {
                    const next = files.filter((_, i) => i !== idx);
                    onFileSelect(next);
                  } else {
                    onFileSelect(null);
                  }
                }}
                className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 flex-shrink-0 ml-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {error && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400 whitespace-pre-line">{error}</p>
      )}

      <p className="mt-2 text-xs text-gray-500 dark:text-shayek-dark-500">{t('report.fileTypes')}</p>
      <p className="text-xs text-gray-500 dark:text-shayek-dark-500">{t('report.maxFileSize')}</p>
    </div>
  );
};

export default FileUpload;

